import React, { useState, useEffect } from 'react';
import { getPaymentTransactionsByReceiptId } from '../../../../../../../../../../crud/payments_api/payment_transactions';
import { toast } from 'react-toastify';
import { Button, Card } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import './TransactionsView.scss';
import { Transaction } from '../../../../../../../types';
import MiniLoadingSpinner from '../../../../../../../../../../components/common/LoadingSpinner';
import ChangeMethod from './components/ChangeMethod/ChangeMethod';

interface TransactionsViewProps {
  currentReceipt: any;
}

const TransactionsView: React.FC<TransactionsViewProps> = ({ currentReceipt }) => {
  const [receipt, setReceipt] = useState<any>(currentReceipt);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [openTransactionIndex, setOpenTransactionIndex] = useState<number | null>(null); // Track which transaction is open
  const [loading,setLoading] = useState(false);
  const [showChangeMethodModal, setShowChangeMethodModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (receipt != null) {
      setLoading(true)
      getPaymentTransactionsByReceiptId(receipt.receipt_id)
        .then((response) => {
          // Filter out transactions that have a cancelled_reason in transaction_metadata
          const filteredTransactions = response.data.filter((transaction: any) => {
            return !transaction.transaction_metadata?.cancelled_reason;
          });
          setTransactions(filteredTransactions);
          
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching transactions:', error);
          toast.error('Error al cargar las transacciones');
          setLoading(false);
        });
    }
  }, [receipt]);

  useEffect(() => {
    setReceipt(currentReceipt);
  }, [currentReceipt]);

  const handleToggleDetails = (index: number) => {
    if (openTransactionIndex === index) {
      setOpenTransactionIndex(null); // Close if already open
    } else {
      setOpenTransactionIndex(index); // Open the clicked transaction
    }
  };

  const handleChangeMethod = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowChangeMethodModal(true);
  };

  const handleCloseChangeMethod = () => {
    setShowChangeMethodModal(false);
    setSelectedTransaction(null);
  };

  const handleMethodChanged = () => {
    // Refresh transactions after method change
    if (receipt?.receipt_id) {
      getPaymentTransactionsByReceiptId(receipt.receipt_id)
        .then((response) => {
          // Filter out transactions that have a cancelled_reason in transaction_metadata
          const filteredTransactions = response.data.filter((transaction: any) => {
            return !transaction.transaction_metadata?.cancelled_reason;
          });
          setTransactions(filteredTransactions);
          handleCloseChangeMethod();
          toast.success('Método de pago actualizado correctamente');
        })
        .catch((error) => {
          console.error('Error refreshing transactions:', error);
          toast.error('Error al actualizar las transacciones');
        });
    }
  };

  // Helper function to get the correct payment type name (custom or standard)
  const getPaymentTypeName = (transaction: Transaction): string => {
    // If it's a custom payment type (payment_type_id === 22) and has a custom_payment_name, show that
    if (Number(transaction.payment_type_id) === 22 && (transaction as any).custom_payment_name) {
      return (transaction as any).custom_payment_name;
    }
    return transaction.payment_type_name || 'Otro';
  };

  if (loading) {
    return <MiniLoadingSpinner text="Cargando transacciones" transparent={true}/>
  }else{

  
  return (
    <div className="transactions-grid">
      {transactions.length === 0 ? (
        <div className="no-transactions">
          <p>No hay transacciones para mostrar</p>
        </div>
      ) : (
        transactions.map((transaction, index) => {
          const netAmount = (transaction as any).net_amount ?? transaction.transaction_amount;
          const tipAmount = (transaction as any).tip_amount ?? 0;
          const paymentTypeName = getPaymentTypeName(transaction);

          return (
          <Card key={transaction.transaction_id} className="transaction-card">
            <Card.Body>
              {/* Compact header with all key info in one row */}
              <div className="card-header-row">
                <div className="transaction-id-compact">
                  <span className="id-label">ID</span>
                  <span className="id-value">{transaction.transaction_id}</span>
                </div>
                
                <div className="amount-badge">
                  {formatCurrency(transaction.transaction_amount)}
                </div>
                
                <div className="payment-method-compact">
                  <span className="method-icon">💳</span>
                  <span className="method-name">{paymentTypeName}</span>
                </div>
                
                <div className={`status ${transaction.approved ? 'approved' : 'not-approved'}`}>
                  {transaction.approved ? 'Aprobada' : 'No aprobada'}
                </div>
              </div>

              {/* Amount breakdown section */}
              <div className="amount-breakdown">
                <div className="amount-item">
                  <span className="amount-label">Neto</span>
                  <span className="amount-value">{formatCurrency(netAmount)}</span>
                </div>
                {tipAmount > 0 && (
                  <div className="amount-item tip">
                    <span className="amount-label">Propina</span>
                    <span className="amount-value">{formatCurrency(tipAmount)}</span>
                  </div>
                )}
              </div>

              {openTransactionIndex === index && (
                <div className="transaction-details">
                  <div><strong>Procesador:</strong> {transaction.processor_name}</div>
                  <div><strong>ID Externo:</strong> {transaction.external_id}</div>
                  {Number(transaction.payment_type_id) === 22 && (transaction as any).custom_payment_type_id && (
                    <div><strong>ID Tipo Personalizado:</strong> {(transaction as any).custom_payment_type_id}</div>
                  )}
                </div>
              )}

              <div className="footer-buttons">
                <Button
                  variant="none"
                  className="detail-button"
                  onClick={() => handleToggleDetails(index)}
                >
                  {openTransactionIndex === index ? 'Ocultar' : 'Detalles'}
                </Button>
                <Button
                  variant="none"
                  className="change-method-button"
                  onClick={() => handleChangeMethod(transaction)}
                >
                  Cambiar método
                </Button>
              </div>
            </Card.Body>
          </Card>
        );
        })
      )}

      {/* Change Method Modal */}
      <ChangeMethod
        show={showChangeMethodModal}
        onHide={handleCloseChangeMethod}
        transaction={selectedTransaction}
        onMethodChanged={handleMethodChanged}
      />
    </div>
  );
}};

export default TransactionsView;
